import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Calculator, ArrowRightLeft, HelpCircle, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AREA_UNITS, calculateEMI, calculateStampDuty, convertArea, formatINR } from "@/lib/constants";
import { chatQuery } from "@/lib/api";

const FAQS = [
  { q: "What areas do you cover?", a: "Pan India, with strong presence in Indore, Bhopal, Pithampur and other MP cities." },
  { q: "Do you handle rentals?", a: "Yes. We handle residential and commercial rentals & leases." },
  { q: "What is 1 Gaj in sq.ft?", a: "1 Gaj = 9 Square Feet. It's identical to 1 Square Yard." },
  { q: "How to list my property?", a: "Click 'List Property' in the menu, fill the form and our team verifies within 24 hours." },
  { q: "Is documentation help provided?", a: "Yes — registry, sale deed, title verification & legal due diligence all included." },
  { q: "Are properties RERA verified?", a: "All applicable properties are checked for MP RERA registration before listing." },
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Namaste! I'm the Wilshire Assistant. Ask about properties, unit conversions (like gaj to sq.ft), EMI, stamp duty, or our services." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const res = await chatQuery(text);
      setMessages((m) => [...m, { role: "bot", text: res.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: "Sorry, I couldn't process that. Please try WhatsApp +91 62612 27048." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-brand-navy text-brand-gold shadow-xl shadow-brand-navy/30 hover:scale-110 transition-transform flex items-center justify-center border-2 border-brand-gold/40"
        aria-label="Open Assistant"
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-44 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] bg-white/95 backdrop-blur-md border border-brand-gold/30 shadow-2xl rounded-sm flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="bg-brand-navy text-brand-ivory px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-brand-gold flex items-center justify-center text-brand-navy">
              <MessageSquare size={16} />
            </div>
            <div>
              <div className="font-serif text-lg leading-none">Wilshire Assistant</div>
              <div className="text-[10px] tracking-[0.25em] text-brand-gold uppercase">Smart real estate helper</div>
            </div>
          </div>

          <Tabs defaultValue="chat" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-4 rounded-none bg-brand-cream border-b border-brand-gold/20 h-auto">
              <TabsTrigger value="chat" data-testid="chat-tab-chat" className="rounded-none text-xs py-2"><MessageSquare size={14} className="mr-1" />Chat</TabsTrigger>
              <TabsTrigger value="convert" data-testid="chat-tab-convert" className="rounded-none text-xs py-2"><ArrowRightLeft size={14} className="mr-1" />Units</TabsTrigger>
              <TabsTrigger value="emi" data-testid="chat-tab-emi" className="rounded-none text-xs py-2"><Calculator size={14} className="mr-1" />EMI</TabsTrigger>
              <TabsTrigger value="stamp" data-testid="chat-tab-stamp" className="rounded-none text-xs py-2"><IndianRupee size={14} className="mr-1" />Duty</TabsTrigger>
            </TabsList>

            <TabsContent value="chat" className="flex-1 flex flex-col p-0 m-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-brand-ivory">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] text-sm px-3 py-2 rounded-sm ${m.role === "user" ? "bg-brand-navy text-white" : "bg-white border border-brand-gold/30 text-brand-charcoal"}`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {sending && <div className="text-xs text-brand-charcoal/60 px-2">typing…</div>}
                <div ref={endRef} />
              </div>
              <div className="border-t border-brand-gold/20 p-2 flex gap-2 bg-white">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask anything..."
                  data-testid="chatbot-input"
                  className="rounded-sm border-brand-gold/30 focus-visible:ring-brand-gold"
                />
                <Button onClick={send} data-testid="chatbot-send" className="rounded-sm bg-brand-navy hover:bg-brand-navy/90 text-brand-gold">
                  <Send size={16} />
                </Button>
              </div>
              <div className="border-t border-brand-gold/20 bg-brand-ivory px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/60 mb-1 flex items-center gap-1"><HelpCircle size={10} /> FAQ</div>
                <div className="flex flex-wrap gap-1">
                  {FAQS.slice(0, 3).map((f, i) => (
                    <button key={i} onClick={() => { setInput(f.q); setTimeout(send, 50); }} className="text-[11px] px-2 py-1 border border-brand-gold/40 hover:bg-brand-gold hover:text-brand-navy transition-colors rounded-sm text-brand-charcoal">
                      {f.q}
                    </button>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="convert" className="flex-1 overflow-y-auto p-4 m-0"><UnitConverter /></TabsContent>
            <TabsContent value="emi" className="flex-1 overflow-y-auto p-4 m-0"><EMICalculator /></TabsContent>
            <TabsContent value="stamp" className="flex-1 overflow-y-auto p-4 m-0"><StampDutyCalculator /></TabsContent>
          </Tabs>
        </div>
      )}
    </>
  );
}

function UnitConverter() {
  const [value, setValue] = useState(1);
  const [from, setFrom] = useState("gaj");
  const [to, setTo] = useState("sqft");
  const result = convertArea(Number(value), from, to);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-brand-charcoal/60 mb-2">Area Unit Converter</div>
        <p className="text-xs text-brand-charcoal/70">Convert between Indian & metric real estate units. <b>1 Gaj = 9 sq.ft</b></p>
      </div>
      <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} data-testid="conv-value" className="rounded-sm" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/60 mb-1">From</div>
          <Select value={from} onValueChange={setFrom}>
            <SelectTrigger data-testid="conv-from" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(AREA_UNITS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand-charcoal/60 mb-1">To</div>
          <Select value={to} onValueChange={setTo}>
            <SelectTrigger data-testid="conv-to" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(AREA_UNITS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-brand-navy text-brand-ivory p-4 rounded-sm">
        <div className="text-[10px] uppercase tracking-widest text-brand-gold mb-1">Result</div>
        <div data-testid="conv-result" className="font-serif text-2xl">{result.toLocaleString('en-IN', { maximumFractionDigits: 4 })}</div>
        <div className="text-xs text-brand-ivory/70">{AREA_UNITS[to].label}</div>
      </div>
    </div>
  );
}

function EMICalculator() {
  const [principal, setPrincipal] = useState(5000000);
  const [rate, setRate] = useState(8.75);
  const [years, setYears] = useState(20);
  const emi = calculateEMI(Number(principal), Number(rate), Number(years));
  const total = emi * years * 12;
  const interest = total - principal;
  return (
    <div className="space-y-4">
      <div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Home Loan EMI Calculator</div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Loan Amount (₹)</label>
        <Input type="number" value={principal} onChange={(e) => setPrincipal(e.target.value)} data-testid="emi-principal" className="rounded-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Rate % p.a.</label>
          <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="emi-rate" className="rounded-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Years</label>
          <Input type="number" value={years} onChange={(e) => setYears(e.target.value)} data-testid="emi-years" className="rounded-sm" />
        </div>
      </div>
      <div className="bg-brand-navy text-brand-ivory p-4 rounded-sm space-y-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand-gold">Monthly EMI</div>
          <div data-testid="emi-monthly" className="font-serif text-2xl">{formatINR(emi)}</div>
        </div>
        <div className="flex justify-between text-xs text-brand-ivory/80 pt-2 border-t border-brand-gold/20">
          <span>Total Interest: {formatINR(interest)}</span>
          <span>Total: {formatINR(total)}</span>
        </div>
      </div>
    </div>
  );
}

function StampDutyCalculator() {
  const [propertyValue, setPropertyValue] = useState(5000000);
  const [gender, setGender] = useState("male");
  const result = calculateStampDuty(Number(propertyValue), gender, true);
  return (
    <div className="space-y-4">
      <div className="text-xs uppercase tracking-widest text-brand-charcoal/60">Stamp Duty (Madhya Pradesh)</div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Property Value (₹)</label>
        <Input type="number" value={propertyValue} onChange={(e) => setPropertyValue(e.target.value)} data-testid="stamp-value" className="rounded-sm" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/60">Buyer</label>
        <Select value={gender} onValueChange={setGender}>
          <SelectTrigger data-testid="stamp-gender" className="rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male (7.5% stamp)</SelectItem>
            <SelectItem value="female">Female (5% stamp)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-brand-navy text-brand-ivory p-4 rounded-sm space-y-2">
        <div className="flex justify-between text-sm"><span>Stamp Duty</span><span className="text-brand-gold">{formatINR(result.stamp)}</span></div>
        <div className="flex justify-between text-sm"><span>Registration</span><span className="text-brand-gold">{formatINR(result.registration)}</span></div>
        <div className="flex justify-between text-sm"><span>Municipal</span><span className="text-brand-gold">{formatINR(result.municipal)}</span></div>
        <div className="flex justify-between text-lg font-serif pt-2 border-t border-brand-gold/20"><span>Total</span><span data-testid="stamp-total" className="text-brand-gold">{formatINR(result.total)}</span></div>
      </div>
      <p className="text-[10px] text-brand-charcoal/50">*Approximate. Actual charges depend on property type, jurisdiction, and current rules.</p>
    </div>
  );
}
